import { useState } from 'react';

import { Box, Col, PressableBox, Text } from '@opendoor/bricks-next';
import type { BoxProps } from '@opendoor/bricks-next';

import { CaretDownIcon, CaretUpIcon } from './icon';
import styles from './ListBox.module.css';
import { IComponentThemeOptions } from './styles/ComponentTheme';

interface ListBoxProps {
  analyticsId: string;
  theme: IComponentThemeOptions;
  ctaDisplayText: string;
  children: React.ReactNode;
  listBoxButton?: React.ReactNode;
  listBoxInput?: React.ReactNode;
  showDropdown: boolean;
  onClick?: () => void;
}

const Options = ({
  height,
  maxHeight,
  children,
}: {
  theme?: IComponentThemeOptions;
  height?: BoxProps['height'];
  maxHeight?: BoxProps['maxHeight'];
  children: React.ReactNode;
}) => {
  return maxHeight ? (
    <Box
      display="block"
      backgroundColor="$backgroundPrimary"
      borderRadius="$8x"
      left={-4}
      maxHeight={maxHeight}
      p="$3"
      pb="$18"
      position="absolute"
      bottom={-4}
      right={-4}
      $platform-web={{ overflow: 'auto', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 3px 1px' }}
      className={styles.optionsList}
    >
      {children}
    </Box>
  ) : (
    <Box
      display="block"
      backgroundColor="$backgroundPrimary"
      borderRadius="$8x"
      left={-4}
      height={height}
      p="$3"
      pb="$18"
      position="absolute"
      bottom={-4}
      right={-4}
      overflow="hidden"
      $platform-web={{ boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 3px 1px' }}
    >
      <Box
        display="block"
        $platform-web={{ overflow: 'auto' }}
        position="absolute"
        top={0}
        bottom="$20"
        left={0}
        right={0}
        px="$2"
        className={styles.optionsScroll}
      >
        {children}
      </Box>
    </Box>
  );
};

const ListBox = ({
  analyticsId,
  ctaDisplayText,
  children,
  listBoxButton,
  listBoxInput,
  showDropdown,
  onClick,
}: ListBoxProps) => {
  return (
    <Col width="100%" my="$3" position="relative" maxWidth="$113" zIndex={500}>
      {showDropdown && children}
      <Box
        display="block"
        p="$1"
        pl="$3"
        backgroundColor="$backgroundPrimary"
        borderRadius="$8x"
        borderColor="$borderAccentBrand"
        borderWidth="$0dot5x"
        width="100%"
        borderStyle="solid"
        zIndex={3}
      >
        <Box
          display="block"
          $platform-web={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
          }}
          alignItems="center"
          gap="$4"
        >
          <Box display="block" $platform-web={{ gridColumn: '1 / 2', textAlign: 'left' }}>
            {listBoxInput ? (
              listBoxInput
            ) : (
              <Text
                tag="span"
                typography="$bodyMedium"
                color="$contentSecondary"
                $platform-web={{ letterSpacing: '-1.4px' }}
                pl="$2"
              >
                {ctaDisplayText}
              </Text>
            )}
          </Box>
          <Box display="block" $platform-web={{ gridColumn: '2 / -1' }}>
            {listBoxButton ? (
              listBoxButton
            ) : (
              // height to avoid jumpiness with buy/sell toggle
              <PressableBox
                onPress={() => onClick && onClick()}
                aria-label={`Go to page ${ctaDisplayText}`}
                analyticsName={`cosmos-landing-page-contextual-field-${analyticsId}`}
                height="$15"
                px="$6"
                width="auto"
                overflow="hidden"
                position="relative"
                backgroundColor="$backgroundAccentBrand"
                borderRadius="$8x"
                alignItems="center"
                justifyContent="center"
                hoverStyle={{
                  backgroundColor: '$backgroundStateHoverAccentBrand',
                }}
                focusVisibleStyle={{
                  backgroundColor: '$backgroundStateHoverAccentBrand',
                }}
                pressStyle={{
                  backgroundColor: '$backgroundStateHoverAccentBrand',
                }}
                className={styles.ctaButton}
              >
                <Box tag="span" zIndex={5} $platform-web={{ color: 'white' }}>
                  {showDropdown ? <CaretDownIcon /> : <CaretUpIcon />}
                </Box>
              </PressableBox>
            )}
          </Box>
        </Box>
      </Box>
    </Col>
  );
};
ListBox.Options = Options;

interface LinkProps {
  label: string;
  href: string;
}

export const AgentSelectListBox = ({
  theme,
  linkOptions,
}: {
  theme: IComponentThemeOptions;
  linkOptions: LinkProps[];
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  let links;

  if (linkOptions?.length > 0) {
    links = linkOptions;
  } else {
    links = [
      {
        label: 'Sell to Opendoor',
        href: '/w/agents#alp-agents',
      },
      {
        label: 'Buy an Opendoor home',
        href: '/w/agents#alp-buy',
      },
      {
        label: 'Why agents use Opendoor',
        href: '/w/agents#alp-testimonials',
      },
    ];
  }

  return (
    <ListBox
      showDropdown={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      analyticsId="agent-select"
      theme={theme}
      ctaDisplayText="For Agents"
    >
      <ListBox.Options theme={theme} height="$62">
        {links.map((option: { label: string; href: string }, i: number) => (
          <PressableBox
            key={i}
            href={option.href}
            target="_blank"
            rel="noopener noreferrer"
            backgroundColor="$backgroundPrimary"
            borderWidth={0}
            borderBottomWidth={i === links.length - 1 ? 0 : 1}
            borderStyle="solid"
            borderColor="$borderSecondary"
            justifyContent="flex-start"
            alignItems="flex-start"
            width="100%"
            px="$3"
            py="$4"
            aria-label={`Go to page ${option.label}`}
            analyticsName={`cosmos-agents-sell-to-opendoor-${option.label
              .toLowerCase()
              .replace(' ', '-')}`}
            hoverStyle={{
              backgroundColor: '$backgroundPrimary',
            }}
            focusVisibleStyle={{
              backgroundColor: '$backgroundPrimary',
            }}
          >
            <Text tag="span" typography="$bodyMedium" color="$contentTertiary">
              {option.label}
            </Text>
          </PressableBox>
        ))}
      </ListBox.Options>
    </ListBox>
  );
};

export const SupportListBox = ({ theme }: { theme: IComponentThemeOptions }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const linkOptions = [
    {
      label: 'Email',
      text: 'support@opendoor.com',
      href: 'mailto:support@opendoor.com',
    },
    {
      label: 'Call',
      text: '1-888-352-7075',
      href: 'tel:+18883527075',
    },
    {
      label: 'Visit',
      text: 'Opendoor Help Center',
      href: 'https://help.opendoor.com',
    },
  ];

  return (
    <ListBox
      showDropdown={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      analyticsId="agent-select"
      theme={theme}
      ctaDisplayText="How can we help?"
    >
      <ListBox.Options theme={theme} height="$62">
        {linkOptions.map((option: { label: string; href: string; text: string }, i: number) => (
          <PressableBox
            key={i}
            href={option.href}
            target="_blank"
            rel="noopener noreferrer"
            backgroundColor="$backgroundPrimary"
            borderWidth={0}
            borderBottomWidth={i === linkOptions.length - 1 ? 0 : 1}
            borderStyle="solid"
            borderColor="$borderSecondary"
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="baseline"
            width="100%"
            px="$3"
            py="$4"
            aria-label={`Go to page ${option.label}`}
            analyticsName={`cosmos-agents-sell-to-opendoor-${option.label
              .toLowerCase()
              .replace(' ', '-')}`}
            hoverStyle={{
              backgroundColor: '$backgroundPrimary',
            }}
            focusVisibleStyle={{
              backgroundColor: '$backgroundPrimary',
            }}
          >
            <Text tag="span" typography="$labelMedium" color="$contentTertiary">
              {option.label}:
            </Text>
            <Text tag="span" typography="$bodyMedium" color="$contentTertiary">
              {' '}
              {option.text}
            </Text>
          </PressableBox>
        ))}
      </ListBox.Options>
    </ListBox>
  );
};
